import { AccredibleLanguage } from '@accredible-frontend-v2/models';
import { AccredibleIconComponentModule } from '@accredible-frontend-v2/new-components/icon';
import { AccountsRedirectionKey } from '@accredible-frontend-v2/services/accounts-redirection';
import { AccredibleBrowserStorageService } from '@accredible-frontend-v2/services/browser-storage';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  Renderer2,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { A11yLangAttributePipe } from './pipe/a11y-lang-attribute.pipe';

@Component({
  standalone: true,
  selector: 'accredible-language-selector',
  imports: [CommonModule, TranslocoModule, A11yLangAttributePipe, AccredibleIconComponentModule],
  templateUrl: './language-selector.component.html',
  styleUrls: [`./language-selector.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccredibleLanguageSelectorComponent implements OnInit, AfterViewInit {
  // This component depends on app level instance of AccredibleLanguageService
  private readonly _document = inject(DOCUMENT);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _renderer = inject(Renderer2);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _browserStorage = inject(AccredibleBrowserStorageService);

  languages = input<AccredibleLanguage[]>();
  themeName = input<string>();

  selectedLanguage: AccredibleLanguage;
  keyboardFocused = false;

  ngOnInit(): void {
    this.selectedLanguage = this._language.getSelectedLanguage();
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this._adjustSelectWidth();
    });
  }

  private _adjustSelectWidth(): void {
    try {
      const selectElement = this._document.querySelector('#language-selector') as HTMLSelectElement;
      const selectedOption = selectElement.options[selectElement.selectedIndex];

      const tempElement = this._document.createElement('span');
      tempElement.style.fontSize = getComputedStyle(selectElement).fontSize;
      tempElement.style.fontFamily = getComputedStyle(selectElement).fontFamily;
      tempElement.textContent = selectedOption.text;
      this._document.body.appendChild(tempElement);

      const textWidth = tempElement.offsetWidth;

      this._document.body.removeChild(tempElement);

      this._renderer.setStyle(selectElement, 'width', `${textWidth + 25}px`);
    } catch (error) {
      console.error('An error occurred:', error);
    }
  }

  changeLanguage(event: Event): void {
    this._adjustSelectWidth();
    const code = (<HTMLInputElement>event.target).value;
    const languageCode = this._language.getAvailableLanguageCode(code, this.themeName());
    this._browserStorage.set(AccountsRedirectionKey.LANGUAGE, languageCode);
    this._language.setLanguage(languageCode, this.themeName());
    this.selectedLanguage = this._language.getSelectedLanguage();

    // Change language on query params if present
    const urlParams = new URLSearchParams(this._document.location.search);
    if (urlParams.has(AccountsRedirectionKey.LANGUAGE)) {
      // This changes the language query param without refreshing route
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { language: languageCode },
        queryParamsHandling: 'merge',
      });
    }
  }
}
